import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X, ChevronRight, Clock } from 'lucide-react';
import { FEATURES, getActiveFeatures, getVisibleFeatures, DEFAULT_DAYS_VISIBLE } from '@/config/recentFeatures';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * "What's New" drawer + trigger button.
 *
 * Trigger: a Sparkles icon in the top nav with a count badge of currently
 * unread / visible features. Click → slide-in drawer from the right.
 *
 * Each entry links to its `path` (when provided) and exposes a "Dismiss"
 * action that writes the featureId into the same localStorage map the
 * inline `<NewBadge>` reads — so dismissing here ALSO removes the matching
 * sidebar / page-title badge. One source of truth.
 */
const STORAGE_KEY = 'smarto.newbadge.dismissed';

const _readDismissed = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) { return {}; }
};
const _writeDismissed = (m) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(m)); } catch (_) {}
};

const CATEGORY_TONE = {
  UX: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  Reliability: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  Reconciliation: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  AI: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  Default: 'bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300',
};

function _formatRelative(iso, t) {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return iso;
  const days = Math.floor((Date.now() - ts) / (24 * 60 * 60 * 1000));
  if (days <= 0) return t ? t('whats_new_today') : 'today';
  if (days === 1) return t ? t('whats_new_yesterday') : 'yesterday';
  if (days < 7) return (t ? t('whats_new_days_ago') : '{n} days ago').replace('{n}', days);
  return new Date(ts).toLocaleDateString();
}

export const WhatsNewButton = () => {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const { t } = useLanguage();

  const recompute = () => {
    setUnread(getVisibleFeatures().length);
  };

  useEffect(() => {
    recompute();
    // Recompute when the storage changes in another tab
    const onStorage = (e) => { if (e.key === STORAGE_KEY) recompute(); };
    // Same-tab: NewBadge dismissals dispatch a custom event so we can sync
    // immediately without waiting for the drawer to close.
    const onLocalChange = () => recompute();
    window.addEventListener('storage', onStorage);
    window.addEventListener('smarto:newbadge-dismissed', onLocalChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('smarto:newbadge-dismissed', onLocalChange);
    };
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="whats-new-button"
        title={unread > 0
          ? `${unread} ${unread === 1 ? t('whats_new_n_item') : t('whats_new_n_items').replace('{n}', unread)}`
          : t('whats_new_title')}
        className="relative p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 transition text-stone-600 dark:text-stone-300"
      >
        <Sparkles size={16} className={unread > 0 ? 'text-orange-500' : 'opacity-70'} />
        {unread > 0 && (
          <span
            data-testid="whats-new-count"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-orange-500 text-white text-[9px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      <WhatsNewDrawer
        open={open}
        onClose={() => { setOpen(false); recompute(); }}
        onDidChangeDismissed={recompute}
      />
    </>
  );
};

export const WhatsNewDrawer = ({ open, onClose, onDidChangeDismissed }) => {
  const [active, setActive] = useState([]);
  const [showDismissed, setShowDismissed] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (!open) return;
    setActive(showDismissed ? getActiveFeatures() : getVisibleFeatures());
  }, [open, showDismissed]);

  const _notifyParent = () => {
    if (onDidChangeDismissed) onDidChangeDismissed();
    try { window.dispatchEvent(new Event('smarto:newbadge-dismissed')); } catch (_) {}
  };

  const dismiss = (id) => {
    const m = _readDismissed();
    m[id] = Date.now();
    _writeDismissed(m);
    setActive(showDismissed ? getActiveFeatures() : getVisibleFeatures());
    _notifyParent();
  };

  const dismissAll = () => {
    const m = _readDismissed();
    for (const f of getVisibleFeatures()) m[f.featureId] = Date.now();
    _writeDismissed(m);
    setActive(showDismissed ? getActiveFeatures() : getVisibleFeatures());
    _notifyParent();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="whats-new-drawer">
      <div
        className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close What's New"
        data-testid="whats-new-overlay"
      />
      <aside
        className="relative w-full max-w-md h-full bg-white dark:bg-stone-900 shadow-2xl border-l border-stone-200 dark:border-stone-700 flex flex-col"
        role="dialog"
        aria-labelledby="whats-new-title"
      >
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 dark:border-stone-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center">
              <Sparkles size={14} className="text-white" />
            </div>
            <div>
              <h2 id="whats-new-title" className="font-bold font-outfit text-base">{t('whats_new_title')}</h2>
              <p className="text-[11px] text-muted-foreground">{t('whats_new_subtitle')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="whats-new-close-btn"
            className="p-1.5 rounded-md hover:bg-stone-100 dark:hover:bg-stone-800 transition"
          >
            <X size={16} />
          </button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto">
          {active.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-6">
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
                <Sparkles size={20} className="text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="text-sm font-semibold mb-1">{t('whats_new_caught_up')}</h3>
              <p className="text-xs text-muted-foreground max-w-xs">
                {t('whats_new_caught_up_desc')}
              </p>
              {!showDismissed && getActiveFeatures().length > getVisibleFeatures().length && (
                <button
                  type="button"
                  onClick={() => setShowDismissed(true)}
                  className="mt-4 text-xs text-orange-600 hover:underline"
                  data-testid="whats-new-show-dismissed"
                >
                  {t('whats_new_show_dismissed')} ({getActiveFeatures().length - getVisibleFeatures().length})
                </button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-stone-100 dark:divide-stone-800" data-testid="whats-new-list">
              {active.map((f) => {
                const tone = CATEGORY_TONE[f.category] || CATEGORY_TONE.Default;
                const catKey = f.category && `cat_${f.category.toLowerCase()}`;
                const catLabel = catKey ? t(catKey) : (f.category || t('new_badge_label'));
                const inner = (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${tone}`}>
                            {catLabel}
                          </span>
                          <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
                            <Clock size={9} /> {_formatRelative(f.since, t)}
                          </span>
                        </div>
                        <h3 className="text-sm font-semibold leading-tight text-foreground">
                          {f.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                          {f.description}
                        </p>
                      </div>
                      {f.path && <ChevronRight size={14} className="opacity-40 mt-1 flex-shrink-0" />}
                    </div>
                  </>
                );
                // When `targetTestId` is present we attach `?tour=<id>` so the
                // FeatureTourHighlight overlay spotlights the relevant element
                // on arrival.
                const linkTo = f.path && f.targetTestId
                  ? `${f.path}?tour=${encodeURIComponent(f.featureId)}`
                  : f.path;
                return (
                  <li key={f.featureId} className="group" data-testid={`whats-new-item-${f.featureId}`}>
                    <div className="px-5 py-3 hover:bg-stone-50 dark:hover:bg-stone-800/50 transition">
                      {linkTo ? (
                        <Link to={linkTo} onClick={onClose} className="block">
                          {inner}
                        </Link>
                      ) : (
                        <div>{inner}</div>
                      )}
                      <div className="mt-2 flex justify-between items-center">
                        {f.targetTestId && f.path ? (
                          <Link
                            to={linkTo}
                            onClick={onClose}
                            data-testid={`whats-new-try-${f.featureId}`}
                            className="text-[10px] font-semibold text-orange-600 hover:text-orange-700"
                          >
                            {t('whats_new_try_feature')} →
                          </Link>
                        ) : <span />}
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismiss(f.featureId); }}
                          data-testid={`whats-new-dismiss-${f.featureId}`}
                          className="text-[10px] text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
                        >
                          {t('whats_new_mark_one')}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* footer */}
        {active.length > 0 && (
          <div className="px-5 py-3 border-t border-stone-200 dark:border-stone-700 flex items-center justify-between">
            <button
              type="button"
              onClick={dismissAll}
              data-testid="whats-new-dismiss-all"
              className="text-xs text-muted-foreground hover:text-foreground transition"
            >
              {t('whats_new_mark_all')}
            </button>
            <span className="text-[10px] text-muted-foreground">
              {active.length === 1 ? t('whats_new_n_item') : t('whats_new_n_items').replace('{n}', active.length)}
            </span>
          </div>
        )}
      </aside>
    </div>
  );
};

export default WhatsNewButton;
